import { useEffect, useState, useRef } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { BookOpen, CheckCircle, AlertCircle, Loader } from 'lucide-react'
import toast from 'react-hot-toast'
import { createLogger } from '../utils/logger'

const logger = createLogger('VerifyEmail')

const VerifyEmail = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState('verifying') // verifying, success, error
  const [errorMessage, setErrorMessage] = useState('')
  const token = searchParams.get('token')
  const verificationAttempted = useRef(false)

  useEffect(() => {
    const verifyEmail = async () => {
      // Prevent double execution in React StrictMode
      if (verificationAttempted.current) {
        return
      }
      verificationAttempted.current = true

      if (!token) {
        setStatus('error')
        setErrorMessage('Verification token is missing')
        return
      }

      try {
        const response = await fetch(`/api/auth/verify-email?token=${encodeURIComponent(token)}`)
        const data = await response.json()

        if (response.ok) {
          setStatus('success')
          toast.success('Email verified successfully!')
          // Redirect to login after 3 seconds
          setTimeout(() => {
            navigate('/login')
          }, 3000)
        } else {
          setStatus('error')
          setErrorMessage(data.error || 'Verification failed')
          toast.error(data.error || 'Email verification failed')
        }
      } catch (error) {
        logger.error('Verification error:', error)
        setStatus('error')
        setErrorMessage('An unexpected error occurred')
        toast.error('Failed to verify email')
      }
    }

    verifyEmail()
  }, [token, navigate])

  if (status === 'verifying') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="text-center">
          <Loader className="w-12 h-12 text-primary-600 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Verifying your email...
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Please wait while we verify your email address.
          </p>
        </div>
      </div>
    )
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="flex justify-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <h2 className="mt-6 text-3xl font-bold text-gray-900 dark:text-white">
              Email verified!
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Your email has been successfully verified. You can now log in to your account.
            </p>
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-500">
              Redirecting to login page...
            </p>
          </div>

          <div>
            <Link to="/login" className="btn-primary w-full block text-center">
              Go to login
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900 dark:text-white">
            Verification failed
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {errorMessage ||
              'We could not verify your email address. The link may be invalid or expired.'}
          </p>
        </div>

        <div className="space-y-3">
          <Link to="/verify-email" className="btn-primary w-full block text-center">
            Request new verification email
          </Link>
          <Link
            to="/login"
            className="flex items-center justify-center text-sm text-primary-600 dark:text-primary-400 hover:text-primary-500"
          >
            Back to login
          </Link>
        </div>
      </div>
    </div>
  )
}

export default VerifyEmail
